import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTheme } from '@/contexts/ThemeContext';
import Pressable from '@/components/base/Pressable';
import { getHistoryState, type HistoryLesson } from '@/mocks/historyStore';
import { getScanHistory, type ScanHistoryItem } from '@/mocks/scanHistory';
import {
  getStudySessions,
  markSessionOpened,
  type SavedStudySession,
} from '@/mocks/studySessions';
import { savedSubjects } from '@/mocks/subjects';

type LearningItem =
  | { source: 'lesson'; id: string; title: string; type: 'video'; date: string; lesson: HistoryLesson }
  | { source: 'session'; id: string; title: string; type: 'text' | 'video'; date: string; session: SavedStudySession }
  | { source: 'scan'; id: string; title: string; type: 'text' | 'video'; date: string; scan: ScanHistoryItem };

function normalizeSubject(value: string) {
  return value.trim().toLowerCase().replace(/^physical sciences$/, 'physical science');
}

function formatRelativeTime(iso: string) {
  const timestamp = new Date(iso).getTime();
  const difference = Math.max(0, Date.now() - timestamp);
  const minutes = Math.floor(difference / 60_000);
  const hours = Math.floor(difference / 3_600_000);
  const days = Math.floor(difference / 86_400_000);

  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes} min ago`;
  if (hours < 24) return `${hours} ${hours === 1 ? 'hour' : 'hours'} ago`;
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days} days ago`;
  return new Date(iso).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short' });
}

export default function SubjectHub() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';
  const [topic, setTopic] = useState('');
  const subject = savedSubjects.find((item) => item.id === id);

  const recentItems = useMemo(() => {
    if (!subject) return [];
    const subjectName = normalizeSubject(subject.name);
    const items: LearningItem[] = [];

    getHistoryState().lessons
      .filter((lesson) => normalizeSubject(lesson.subject) === subjectName)
      .forEach((lesson) => items.push({
        source: 'lesson',
        id: lesson.id,
        title: lesson.title,
        type: 'video',
        date: lesson.lastWatchedAt,
        lesson,
      }));

    getStudySessions()
      .filter((session) => normalizeSubject(session.subject) === subjectName)
      .forEach((session) => items.push({
        source: 'session',
        id: session.id,
        title: session.topicName?.trim() || session.title,
        type: session.type,
        date: session.lastOpenedAt || session.createdAt,
        session,
      }));

    getScanHistory()
      .filter((scan) => normalizeSubject(scan.subject) === subjectName)
      .forEach((scan) => items.push({
        source: 'scan',
        id: scan.id,
        title: scan.title,
        type: scan.type,
        date: scan.createdAt,
        scan,
      }));

    items.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

    const seen = new Set<string>();
    return items.filter((item) => {
      const key = `${item.title.trim().toLowerCase()}::${item.type}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [subject]);

  if (!subject) {
    return (
      <div className={`flex min-h-full items-center justify-center px-6 ${isDark ? 'bg-[#0F0F10]' : 'bg-white'}`}>
        <div className="text-center">
          <i className={`ri-error-warning-line text-2xl ${isDark ? 'text-white/30' : 'text-neutral-300'}`} />
          <h1 className={`mt-3 text-base font-semibold ${isDark ? 'text-white' : 'text-neutral-900'}`}>Subject not found</h1>
          <Pressable
            onClick={() => navigate('/')}
            className={`mt-5 rounded-xl px-5 py-3 text-xs font-semibold ${isDark ? 'bg-white text-black' : 'bg-black text-white'}`}
            scale={0.96}
          >
            Back Home
          </Pressable>
        </div>
      </div>
    );
  }

  const recentlyStudied = recentItems.slice(0, 5);
  const pageBg = isDark ? 'bg-[#0F0F10]' : 'bg-white';
  const textPrimary = isDark ? 'text-[#F5F5F7]' : 'text-neutral-900';
  const textSecondary = isDark ? 'text-[#A1A1A6]' : 'text-neutral-500';
  const textMuted = isDark ? 'text-[#6E6E73]' : 'text-neutral-400';
  const cardBg = isDark ? 'bg-[#1C1D21]' : 'bg-white';
  const cardBorder = isDark ? 'border-white/[0.07]' : 'border-neutral-200/80';
  const subtleBg = isDark ? 'bg-white/[0.04]' : 'bg-neutral-50';

  const openItem = (item: LearningItem) => {
    if (item.source === 'lesson') {
      navigate(`/lesson/${item.id}`);
      return;
    }
    if (item.source === 'session') markSessionOpened(item.id);
    navigate(item.type === 'text' ? '/scan/result-text' : '/scan/result-video');
  };

  const scanHomework = () => {
    const context = {
      subject: subject.name,
      subjectId: subject.id,
      grade: '10',
      topic: '',
    };
    sessionStorage.setItem('scan_context', JSON.stringify(context));
    navigate('/scan/start', { state: context });
  };

  const generateTopic = (outputMode: 'text' | 'video') => {
    const value = topic.trim();
    if (!value) return;
    sessionStorage.removeItem('scan_input_image');
    sessionStorage.removeItem('lavyn_generation_job');
    sessionStorage.setItem('scan_context', JSON.stringify({
      subject: subject.name,
      subjectId: subject.id,
      grade: '10',
      topic: value,
      fromTypedPrompt: true,
    }));
    sessionStorage.setItem('scan_instruction', `Teach this ${subject.name} question or topic clearly: ${value}`);
    sessionStorage.setItem('scan_output_mode', outputMode);
    navigate('/scan/analyzing');
  };

  return (
    <div className={`flex min-h-0 flex-col ${pageBg}`}>
      <header
        className="sticky top-0 z-30 flex flex-shrink-0 items-center gap-3 border-b px-5 py-4 backdrop-blur-xl lg:px-8"
        style={{
          backgroundColor: isDark ? 'rgba(15,15,16,0.88)' : 'rgba(255,255,255,0.88)',
          borderColor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)',
        }}
      >
        <Pressable
          onClick={() => navigate(-1)}
          className={`flex h-9 w-9 items-center justify-center rounded-xl ${subtleBg}`}
          scale={0.88}
          aria-label="Go back"
        >
          <i className={`ri-arrow-left-line text-sm ${textSecondary}`} />
        </Pressable>
        <div
          className="flex h-10 w-10 items-center justify-center rounded-xl"
          style={{ backgroundColor: `${subject.color}18` }}
        >
          <i className={`${subject.icon} text-xl`} style={{ color: subject.color }} />
        </div>
        <div className="min-w-0">
          <p className={`text-[10px] font-semibold uppercase tracking-[0.15em] ${textMuted}`}>Subject Learning Hub</p>
          <h1 className={`truncate text-base font-bold ${textPrimary}`}>{subject.name}</h1>
        </div>
      </header>

      <main className="min-h-0 flex-1 overflow-y-auto">
        <div className="mx-auto w-full max-w-3xl space-y-7 px-5 py-6 pb-10 lg:px-8">
          <section>
            <h2 className={`mb-3 text-[11px] font-bold uppercase tracking-[0.14em] ${textMuted}`}>What Would You Like to Do?</h2>
            <div className="grid gap-3 sm:grid-cols-2">
              <Pressable
                onClick={scanHomework}
                className={`flex min-h-28 items-start gap-3 rounded-2xl border p-4 text-left ${cardBg} ${cardBorder}`}
                scale={0.98}
              >
                <div className={`flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl ${subtleBg}`}>
                  <i className={`ri-camera-3-line text-lg ${textPrimary}`} />
                </div>
                <div>
                  <h3 className={`text-sm font-semibold ${textPrimary}`}>Scan Homework</h3>
                  <p className={`mt-1 text-[11px] leading-relaxed ${textSecondary}`}>Take or upload a picture and turn it into an AI lesson.</p>
                </div>
              </Pressable>

              <div className={`rounded-2xl border p-4 ${cardBg} ${cardBorder}`}>
                <div className="flex items-start gap-3">
                  <div className={`flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl ${subtleBg}`}>
                    <i className={`ri-sparkling-2-line text-lg ${textPrimary}`} />
                  </div>
                  <div>
                    <h3 className={`text-sm font-semibold ${textPrimary}`}>Learn a Topic</h3>
                    <p className={`mt-1 text-[11px] leading-relaxed ${textSecondary}`}>Ask the tutor to explain a question or topic.</p>
                  </div>
                </div>
                <label className="sr-only" htmlFor="subject-topic-input">Question or topic</label>
                <textarea
                  id="subject-topic-input"
                  value={topic}
                  onChange={(event) => setTopic(event.target.value)}
                  placeholder={`e.g. Explain ${subject.name === 'Mathematics' ? 'quadratic equations' : `a ${subject.name.toLowerCase()} topic`}`}
                  rows={2}
                  className={`mt-4 w-full resize-none rounded-xl border bg-transparent px-3 py-2.5 text-xs leading-relaxed outline-none transition-colors ${cardBorder} ${textPrimary} ${isDark ? 'placeholder:text-[#48484F] focus:border-white/20' : 'placeholder:text-neutral-400 focus:border-neutral-400'}`}
                />
                <div className="mt-3 grid grid-cols-2 gap-2">
                  <Pressable
                    onClick={() => generateTopic('text')}
                    disabled={!topic.trim()}
                    className={`flex min-h-10 items-center justify-center gap-1.5 rounded-xl border text-[11px] font-semibold disabled:cursor-not-allowed disabled:opacity-30 ${cardBorder} ${textPrimary}`}
                    scale={0.97}
                  >
                    <i className="ri-file-text-line" />
                    Generate Text
                  </Pressable>
                  <Pressable
                    onClick={() => generateTopic('video')}
                    disabled={!topic.trim()}
                    className={`flex min-h-10 items-center justify-center gap-1.5 rounded-xl text-[11px] font-semibold disabled:cursor-not-allowed disabled:opacity-30 ${isDark ? 'bg-white text-black' : 'bg-black text-white'}`}
                    scale={0.97}
                  >
                    <i className="ri-video-line" />
                    Generate Video
                  </Pressable>
                </div>
              </div>
            </div>
          </section>

          <section>
            <div className="mb-3 flex items-center justify-between">
              <h2 className={`text-[11px] font-bold uppercase tracking-[0.14em] ${textMuted}`}>Recently Studied</h2>
              {recentlyStudied.length > 0 && (
                <span className={`text-[10px] ${textMuted}`}>{recentlyStudied.length} recent</span>
              )}
            </div>
            {recentlyStudied.length > 0 ? (
              <div className={`overflow-hidden rounded-2xl border ${cardBg} ${cardBorder}`}>
                {recentlyStudied.map((item, index) => (
                  <Pressable
                    key={`${item.source}-${item.id}`}
                    onClick={() => openItem(item)}
                    className={`flex min-h-16 w-full items-center gap-3 px-4 py-3 text-left ${index > 0 ? `border-t ${cardBorder}` : ''}`}
                    scale={0.99}
                  >
                    <div className={`flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl ${subtleBg}`}>
                      <i className={`${item.type === 'video' ? 'ri-play-circle-line' : 'ri-file-text-line'} text-sm`} style={{ color: subject.color }} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className={`truncate text-xs font-semibold ${textPrimary}`}>{item.title}</p>
                      <p className={`mt-0.5 text-[10px] ${textSecondary}`}>{formatRelativeTime(item.date)}</p>
                    </div>
                    <i className={`ri-arrow-right-s-line text-lg ${textMuted}`} />
                  </Pressable>
                ))}
              </div>
            ) : (
              <div className={`rounded-2xl border px-5 py-5 text-center ${cardBg} ${cardBorder}`}>
                <p className={`text-xs ${textSecondary}`}>Your recent {subject.name} lessons will appear here.</p>
              </div>
            )}
          </section>
        </div>
      </main>
    </div>
  );
}